/**
 * Время — московское, и это не мелочь (НФ-7).
 *
 * ═══════════════════════════════════════════════════════════════════════
 * WILDBERRIES ЖИВЁТ ПО МОСКВЕ, И НАШИ ЧИСЛА — ТОЖЕ.
 *
 * Сутки в статистике считаются московские: так их отдаёт WB, так они
 * сложены у нас (`src/common/msk-date.ts` на сервере). А отметки времени
 * до сегодняшнего дня рисовались браузером — то есть в часовом поясе
 * читателя.
 *
 * Для продавца из Москвы это одно и то же, и поэтому расхождение
 * не замечали. Для продавца из Новосибирска «расход за 19 августа»
 * (московские сутки) стоял рядом с «ставка поднята 19 авг., 13:41»
 * (его время, то есть 09:41 по Москве) — и сверить это с кабинетом
 * Wildberries, который показывает московское, было невозможно.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ПОЯС ЗАШИТ, А НЕ ВЗЯТ ИЗ НАСТРОЕК.
 *
 * Россия отменила переход на летнее время в 2014 году, Москва круглый
 * год UTC+3. Брать пояс из окружения браузера значит вернуть ровно ту
 * ошибку, ради которой этот файл написан.
 * ═══════════════════════════════════════════════════════════════════════
 */

/* ФОРМАТЫ ДАТ БЕРУТСЯ ГОТОВЫМИ. Здесь стояли `toLocaleDateString`
   и родня — а они по стандарту СОБИРАЮТ формат на каждый вызов,
   и это самая дорогая часть работы (замер: 32,5 мкс против 0,7).
   `mskDate` зовётся на каждую строку таблицы запросов, `mskStamp` —
   на каждую строку журнала. Довод целиком — в `chisla.ts`. */
import { форматДаты } from './chisla';

const MSK = 'Europe/Moscow';
const RU = 'ru-RU';

/** Подпись к столбцу или строке со временем. */
export const MSK_LABEL = 'МСК';

function at(value: string | Date): Date | null {
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * КРИВАЯ ДАТА НЕ РОНЯЕТ ЭКРАН И НЕ ПРИТВОРЯЕТСЯ ДАТОЙ.
 *
 * `new Date('вчера')` даёт `Invalid Date`, а `toLocaleString` на нём —
 * строку «Invalid Date» посреди русского интерфейса. Прочерк честнее.
 */
const NONE = '—';

/** «19.08.2026, 09:41». */
export function mskDateTime(value: string | Date): string {
  const d = at(value);
  return d === null
    ? NONE
    : форматДаты({ timeZone: MSK, dateStyle: 'short', timeStyle: 'short' }).format(d);
}

/**
 * «15.08.2026 в 06:41» — с предлогом, как в эталоне.
 *
 * ПОЧЕМУ НЕ `mskDateTime`. Тот ставит запятую («15.08.2026, 06:41»),
 * и это верно в таблице, где столбец называется «Когда». В строке
 * справки, где подпись слева говорит «Собрана», запятая читается как
 * перечисление: «собрана 15.08.2026, 06:41» — будто это два разных
 * сведения. Предлог связывает их в одно.
 */
export function mskДатаВЧас(value: string | Date): string {
  const d = at(value);
  if (d === null) return NONE;
  return `${mskDate(d)} в ${форматДаты({ timeZone: MSK, hour: '2-digit', minute: '2-digit' }).format(d)}`;
}

/** «19.08.2026». */
export function mskDate(value: string | Date): string {
  const d = at(value);
  return d === null ? NONE : форматДаты({ timeZone: MSK, dateStyle: 'short' }).format(d);
}

/**
 * «15.08 07:12» — короткая метка времени для списка.
 *
 * БЕЗ ГОДА И БЕЗ ЗАПЯТОЙ, потому что это СТОЛБЕЦ, а не предложение:
 * сорок строк подряд, читаются сверху вниз одним взглядом, и всё,
 * что в них повторяется у каждой, — шум. Год у списка за последние
 * тридцать суток повторяется у всех.
 */
export function mskДеньЧас(value: string | Date): string {
  const d = at(value);
  if (d === null) return NONE;
  const день = форматДаты({ timeZone: MSK, day: '2-digit', month: '2-digit' }).format(d);
  const час = форматДаты({ timeZone: MSK, hour: '2-digit', minute: '2-digit' }).format(d);
  return `${день} ${час}`;
}

/** «19 августа 2026 г.». */
export function mskLongDate(value: string | Date): string {
  const d = at(value);
  return d === null ? NONE : форматДаты({ timeZone: MSK, dateStyle: 'long' }).format(d);
}

/** «19 августа» — без года: список короткий и свежий. */
export function mskDayMonth(value: string | Date): string {
  const d = at(value);
  return d === null
    ? NONE
    : форматДаты({ timeZone: MSK, day: 'numeric', month: 'long' }).format(d);
}

/** «19.08» — для узкого столбца. */
export function mskDayMonthShort(value: string | Date): string {
  const d = at(value);
  return d === null
    ? NONE
    : форматДаты({ timeZone: MSK, day: '2-digit', month: '2-digit' }).format(d);
}

/** «09:41». */
export function mskTime(value: string | Date): string {
  const d = at(value);
  return d === null
    ? NONE
    : форматДаты({ timeZone: MSK, hour: '2-digit', minute: '2-digit' }).format(d);
}

/** «19 авг., 09:41» — дата и время в одну узкую строку. */
export function mskStamp(value: string | Date): string {
  const d = at(value);
  return d === null
    ? NONE
    : форматДаты({
        timeZone: MSK,
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }).format(d);
}

/** «19 августа в 09:41» — для строки, которую читают целиком. */
export function mskLongStamp(value: string | Date): string {
  const d = at(value);
  return d === null
    ? NONE
    : форматДаты({
        timeZone: MSK,
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
      }).format(d);
}
